import { cloneElement, ComponentProps, forwardRef, ReactElement } from 'react';
import styles from './CircleButton.module.scss';
import { withStyles } from '../../../utils';
import MaterialSymbol from '../../DataDisplay/MaterialSymbol/MaterialSymbol';
import { useDatadogLog } from '../logging';

const cn = withStyles(styles);

type ColorScheme = 'neutral' | 'action' | 'destructive' | 'success';
type Size = 'large' | 'medium' | 'small';
type MaterialSymbolProps = ComponentProps<typeof MaterialSymbol>;
type IconProps = ComponentProps<typeof MaterialSymbol>;

type CircleButtonProps = React.ComponentProps<'button'> & {
  colorScheme?: ColorScheme;
  size?: Size;
  circleFill?: boolean;
  iconFill?: boolean;
} & (
    | {
        icon?: never;
        // children: ReactElement<any>;
        children: ReactElement<MaterialSymbolProps>;

        // children: ReactElement<IconProps>;
      }
    | {
        icon: IconProps['icon'];
        children?: never;
      }
  );

const CircleButton = forwardRef<HTMLButtonElement, CircleButtonProps>(
  (
    {
      children,
      icon,
      colorScheme = 'neutral',
      size = 'medium',
      circleFill = true,
      iconFill = false,
      className,
      style,
      ...props
    },
    forwardedRef,
  ) => {
    const log = useDatadogLog();

    const handleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
      const label =
        typeof children === 'string' ? children : (props['aria-label'] ?? e.currentTarget.textContent ?? 'Button');

      log({
        message: `${label} clicked`,
        payload: { name: label },
        actionType: 'Button',
      });

      if (props.onClick) {
        props.onClick(e);
      }
    };

    return (
      <div className={cn('circleButtonContainer', `size-${size}`, className)} style={style}>
        <button
          ref={forwardedRef}
          type="button"
          {...props}
          className={cn(
            'circleButton',
            `color-${colorScheme}`,

            circleFill && 'circleFill',
            iconFill && 'iconFill',
          )}
          onClick={handleClick}
          data-custom="true"
        >
          {icon ? (
            <MaterialSymbol className={cn('icon')} icon={icon} />
          ) : children ? (
            cloneElement(children, { className: cn('icon') })
          ) : null}
        </button>
      </div>
    );
  },
);

CircleButton.displayName = 'CircleButton';

export default CircleButton;
